import { useState, useCallback, useEffect, useRef } from "react";
import {
  calculateDreamspell,
  isLeapDay,
  type GlyphResult,
} from "@/mocks/dreamspellData";

interface CalculatorProps {
  onCalculate: (result: GlyphResult | null, dateStr: string, isLeap: boolean) => void;
}

export default function Calculator({ onCalculate }: CalculatorProps) {
  const [year, setYear] = useState<string>("");
  const [month, setMonth] = useState<string>("");
  const [day, setDay] = useState<string>("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCalculate = useCallback(() => {
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);

    if (!y || !m || !d || m < 1 || m > 12 || d < 1 || d > 31) {
      onCalculate(null, "", false);
      return;
    }

    const isLeap = isLeapDay(y, m, d);
    const dateStr = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

    if (isLeap) {
      onCalculate(null, dateStr, true);
      return;
    }

    const result = calculateDreamspell(y, m, d);
    onCalculate(result, dateStr, false);
  }, [year, month, day, onCalculate]);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);

    if (y && m && d && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      timerRef.current = setTimeout(() => {
        handleCalculate();
      }, 400);
    } else if (year === "" || month === "" || day === "") {
      onCalculate(null, "", false);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [year, month, day, handleCalculate, onCalculate]);

  return (
    <section className="w-full px-4 md:px-6 animate-fade-in">
      <div className="max-w-5xl mx-auto">
        <div className="border border-stone-300 rounded-xl p-5 md:p-8 bg-white/60">
          <h2 className="text-lg md:text-xl font-semibold text-stone-800 mb-5">
            輸入西元出生日期
          </h2>

          <div className="flex flex-row gap-2 md:gap-4 items-end">
            <div className="flex flex-col gap-1 flex-1 min-w-0">
              <label
                htmlFor="year"
                className="text-[10px] font-medium text-stone-500 uppercase tracking-wider"
              >
                年
              </label>
              <input
                id="year"
                type="number"
                inputMode="numeric"
                placeholder="1990"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full px-2 py-2 border border-stone-300 rounded-lg text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-400 focus:border-transparent text-sm"
              />
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-0">
              <label
                htmlFor="month"
                className="text-[10px] font-medium text-stone-500 uppercase tracking-wider"
              >
                月
              </label>
              <input
                id="month"
                type="number"
                inputMode="numeric"
                placeholder="5"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full px-2 py-2 border border-stone-300 rounded-lg text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-400 focus:border-transparent text-sm"
              />
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-0">
              <label
                htmlFor="day"
                className="text-[10px] font-medium text-stone-500 uppercase tracking-wider"
              >
                日
              </label>
              <input
                id="day"
                type="number"
                inputMode="numeric"
                placeholder="13"
                value={day}
                onChange={(e) => setDay(e.target.value)}
                className="w-full px-2 py-2 border border-stone-300 rounded-lg text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-400 focus:border-transparent text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={handleCalculate}
                className="px-4 py-2 bg-stone-800 text-white rounded-lg font-medium text-sm hover:bg-stone-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                推算
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}